// Rechnungen (Screen eigentuemer/04): „Zur Freigabe“ und „Erledigt“. Einzige Schreibaktion des Portals:
// Freigeben oder Ablehnen mit Begründung (≥ 5 Zeichen). Die Datenbank prüft alles noch einmal.
import { useState } from 'react';
import { Check, ChatText, CircleNotch, FilePdf, Receipt, SealCheck, X } from '@phosphor-icons/react';
import { FehlerHinweis, IconChip, LeerZustand, SegmentSteuerung, StatusChip } from '../../components/portal/bausteine';
import {
  MIN_BEGRUENDUNG, begruendungOk, betrag, erledigtText, euro, rechnungOrt, rechnungUnterzeile, rechnungenFuer,
  type PortalRechnung, type RechnungFilter, type RechnungStand,
} from '../../lib/portal/eigentuemerE7';

export interface RechnungAktionen {
  entscheiden: (id: string, e: Exclude<RechnungStand, 'offen'>, grund?: string) => Promise<void>;
  ansehen: (r: PortalRechnung) => Promise<void>;
}

function RechnungKarte({ r, aktionen, onErledigt }: { r: PortalRechnung; aktionen: RechnungAktionen; onErledigt: (text: string) => void }) {
  const [ablehnen, setAblehnen] = useState(false);
  const [grund, setGrund] = useState('');
  const [grundFehler, setGrundFehler] = useState(false);
  const [laeuft, setLaeuft] = useState<'frei' | 'ab' | 'pdf' | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const offen = r.stand === 'offen';
  const titel = (r.bezeichnung ?? '').trim() || 'Rechnung';

  const entscheide = async (e: 'freigegeben' | 'abgelehnt') => {
    if (e === 'abgelehnt' && !begruendungOk(grund)) { setGrundFehler(true); return; }
    setLaeuft(e === 'freigegeben' ? 'frei' : 'ab'); setFehler(null);
    try {
      await aktionen.entscheiden(r.id, e, e === 'abgelehnt' ? grund : undefined);
      onErledigt(e === 'freigegeben' ? `„${titel}“ ist freigegeben.` : `„${titel}“ ist abgelehnt. Die Verwaltung meldet sich bei Ihnen.`);
    } catch (x) { setFehler((x as Error).message); } finally { setLaeuft(null); }
  };
  const ansehen = async () => {
    setLaeuft('pdf'); setFehler(null);
    try { await aktionen.ansehen(r); } catch (x) { setFehler((x as Error).message); } finally { setLaeuft(null); }
  };

  return (
    <section className={`p-glas p-hebt ${offen ? 'p-farbe-orange' : r.stand === 'abgelehnt' ? 'p-farbe-heizung' : 'p-farbe-konto'}`}
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }} aria-label={titel}>
      <div className="p-glas-schimmer"><Receipt size={170} weight="duotone" className="p-wasserzeichen" style={{ opacity: 0.1 }} /></div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
        <IconChip farbe={offen ? 'orange' : r.stand === 'abgelehnt' ? 'heizung' : 'konto'} icon={Receipt} groesse={40} />
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 16, fontWeight: 800, overflowWrap: 'anywhere' }}>{titel}</span>
          <span className="p-kuerzen" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w50)' }}>{rechnungUnterzeile(r)}</span>
        </span>
        {offen && <StatusChip ton="warn">Wartet auf Sie</StatusChip>}
        {r.stand === 'freigegeben' && <StatusChip ton="ok">Freigegeben</StatusChip>}
        {r.stand === 'abgelehnt' && <StatusChip ton="fehler">Abgelehnt</StatusChip>}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--p-w45)' }}>Betrag brutto</span>
          <span className="p-zahl" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em' }}>{euro(betrag(r))}</span>
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, fontSize: 12.5, fontWeight: 600, color: 'var(--p-w60)' }}>
          {rechnungOrt(r).map((z) => <span key={z}>{z}</span>)}
        </span>
      </div>

      {!offen && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', borderRadius: 14, background: 'var(--p-w05)', fontSize: 12.5, fontWeight: 600, color: 'var(--p-w75)' }}>
          <span>{erledigtText(r)}</span>
          {r.begruendung && <span style={{ display: 'flex', gap: 8 }}><ChatText size={15} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />{r.begruendung}</span>}
        </div>
      )}

      {fehler && <FehlerHinweis text={fehler} onSchliessen={() => setFehler(null)} />}

      {offen && ablehnen && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea value={grund} autoFocus rows={3} maxLength={1000} aria-label="Grund für die Ablehnung"
            onChange={(e) => { setGrund(e.target.value); if (grundFehler && begruendungOk(e.target.value)) setGrundFehler(false); }}
            placeholder="Grund für die Ablehnung, z. B. „Bitte zweites Angebot einholen“"
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', borderRadius: 14, padding: '10px 12px', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
              color: 'var(--p-w)', background: 'var(--p-w05)', border: 'none', boxShadow: grundFehler ? 'var(--p-fehler-ring)' : 'inset 0 0 0 1px var(--p-w08)' }} />
          {grundFehler && <span role="alert" style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-fehler-text)' }}>Bitte geben Sie einen Grund an (mindestens {MIN_BEGRUENDUNG} Zeichen).</span>}
        </div>
      )}

      {(offen || r.beleg_id) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {r.beleg_id && (
            <button type="button" className="p-knopf" onClick={() => void ansehen()} disabled={!!laeuft} data-tip="Rechnung als PDF ansehen"
              style={{ height: 42, border: 'none', background: 'var(--p-w08)', fontSize: 13 }}>
              {laeuft === 'pdf' ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <FilePdf size={17} />} Ansehen
            </button>
          )}
          <span style={{ flex: 1 }} />
          {offen && !ablehnen && (
            <>
              <button type="button" className="p-knopf" onClick={() => setAblehnen(true)} disabled={!!laeuft} data-tip="Rechnung ablehnen und Grund angeben"
                style={{ height: 42, border: 'none', background: 'var(--p-ton-fehler-bg)', color: 'var(--p-ton-fehler-fg)', fontSize: 13 }}>
                Ablehnen
              </button>
              <button type="button" className="p-knopf p-knopf-gruen" onClick={() => void entscheide('freigegeben')} disabled={!!laeuft} data-tip="Rechnung zur Zahlung freigeben"
                style={{ height: 42, fontSize: 13 }}>
                {laeuft === 'frei' ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Check size={16} weight="bold" />} Freigeben
              </button>
            </>
          )}
          {offen && ablehnen && (
            <>
              <button type="button" className="p-knopf" onClick={() => { setAblehnen(false); setGrund(''); setGrundFehler(false); }} disabled={!!laeuft}
                data-tip="Zurück, ohne abzulehnen" style={{ height: 42, border: 'none', background: 'var(--p-w08)', fontSize: 13 }}>
                Zurück
              </button>
              <button type="button" className="p-knopf" onClick={() => void entscheide('abgelehnt')} disabled={!!laeuft} data-tip="Ablehnung mit Begründung an die Verwaltung senden"
                style={{ height: 42, border: 'none', background: 'var(--p-notfall)', color: 'var(--p-w)', fontSize: 13 }}>
                {laeuft === 'ab' ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <X size={16} weight="bold" />} Ablehnen
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default function EigentuemerRechnungen({ rechnungen, objektId, filter, onFilter, aktionen, onGeaendert }: {
  rechnungen: PortalRechnung[];
  objektId: string;
  filter: RechnungFilter;
  onFilter: (f: RechnungFilter) => void;
  aktionen: RechnungAktionen;
  onGeaendert: () => void;
}) {
  const [meldung, setMeldung] = useState<string | null>(null);
  const liste = rechnungenFuer(rechnungen, objektId, filter);
  const nOffen = rechnungenFuer(rechnungen, objektId, 'offen').length;
  const nErledigt = rechnungenFuer(rechnungen, objektId, 'erledigt').length;
  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ maxWidth: 420 }}>
        <SegmentSteuerung label="Rechnungen filtern" wert={filter} onWahl={onFilter} hoehe={40}
          optionen={[
            { key: 'offen', label: `Zur Freigabe (${nOffen})`, tip: 'Rechnungen, die auf Ihre Freigabe warten' },
            { key: 'erledigt', label: `Erledigt (${nErledigt})`, tip: 'Von Ihnen freigegebene oder abgelehnte Rechnungen' },
          ]} />
      </div>
      {meldung && <FehlerHinweis ton="ok" text={meldung} onSchliessen={() => setMeldung(null)} />}
      {liste.length === 0
        ? <LeerZustand icon={SealCheck} text={filter === 'offen' ? 'Keine Rechnung wartet auf Sie' : 'Keine Rechnungen in dieser Ansicht'} />
        : (
          <div className="mp-karten">
            {liste.map((r) => <RechnungKarte key={r.id} r={r} aktionen={aktionen} onErledigt={(t) => { setMeldung(t); onGeaendert(); }} />)}
          </div>
        )}
    </div>
  );
}
